/**
 * Infinite auto-scroll (marquee) for the spv-marcas brand chip rail.
 *
 * Drives a native `overflow-x` scroller's `scrollLeft` from a single
 * `requestAnimationFrame` loop, so it composes with `spv-drag-scroll.js`
 * (which scrubs the same `scrollLeft` and toggles `is-dragging`) and with
 * native touch scrolling — they all act on one property, none fight.
 *
 * Seamless loop: the section renders one `[data-spv-marcas-set]` group; we
 * clone it enough times to overflow the rail, then wrap `scrollLeft` by exactly
 * one set width. Because the sets are identical, the wrap is invisible.
 *
 * Pauses on: mouse hover, active drag (`is-dragging`), recent user scroll /
 * wheel / touch, tab hidden, rail off-screen. Honors `prefers-reduced-motion`
 * (stays a plain scroller). Applied to any `[data-spv-marcas-marquee]` element.
 */
(function () {
  if (window.__spvMarcasMarquee) return;
  window.__spvMarcasMarquee = true;

  var IDLE_MS = 1200; // hands-off window after the user scrolls/wheels/touches
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

  function now() {
    return performance.now();
  }

  function init(track) {
    if (track.__spvMarquee) return;
    track.__spvMarquee = true;

    // Honor reduced motion — leave it a static (still draggable) scroller.
    if (reduceMQ.matches) return;

    var set = track.querySelector('[data-spv-marcas-set]');
    if (!set) return;

    var speed = parseFloat(track.getAttribute('data-speed')) || 4;
    var pxPerSec = speed * 12; // 1–10 → ~12–120 px/s
    var direction = track.getAttribute('data-direction') === 'ltr' ? -1 : 1;

    var trackGap = parseFloat(getComputedStyle(track).columnGap) || 0;

    var oneSetWidth = 0;
    var pos = 0;
    var lastWritten = -1;
    var hovering = false;
    var userActiveUntil = 0;
    var visible = true;
    var lastTs = 0;
    var rafId = null;

    function measure() {
      // Start-to-start distance between identical sets = set width + the gap
      // the flex track inserts between them.
      oneSetWidth = set.offsetWidth + trackGap;
    }

    function buildClones() {
      var old = track.querySelectorAll('[data-spv-marcas-set-clone]');
      for (var i = 0; i < old.length; i++) old[i].remove();

      var setW = set.offsetWidth;
      var copies = setW > 0 ? Math.max(2, Math.ceil(track.clientWidth / setW) + 1) : 2;

      for (var c = 1; c < copies; c++) {
        var clone = /** @type {HTMLElement} */ (set.cloneNode(true));
        clone.removeAttribute('data-spv-marcas-set');
        clone.setAttribute('data-spv-marcas-set-clone', '');
        clone.setAttribute('aria-hidden', 'true');
        // Keep cloned links/buttons out of the tab order and a11y tree.
        var focusables = clone.querySelectorAll('a, button, [tabindex]');
        for (var f = 0; f < focusables.length; f++) {
          focusables[f].setAttribute('tabindex', '-1');
        }
        track.appendChild(clone);
      }
      measure();
    }

    function normalize() {
      if (oneSetWidth <= 0) return;
      while (pos >= oneSetWidth) pos -= oneSetWidth;
      while (pos < 0) pos += oneSetWidth;
    }

    function paused(ts) {
      return (
        hovering ||
        document.hidden ||
        !visible ||
        track.classList.contains('is-dragging') ||
        ts < userActiveUntil
      );
    }

    function frame(ts) {
      if (!track.isConnected) {
        if (rafId) cancelAnimationFrame(rafId);
        return;
      }
      rafId = requestAnimationFrame(frame);

      var dt = lastTs ? (ts - lastTs) / 1000 : 0;
      lastTs = ts;
      if (dt > 0.1) dt = 0.1; // clamp after the tab was backgrounded

      if (paused(ts)) {
        // Resume from wherever the user / native momentum left the rail.
        pos = track.scrollLeft;
        return;
      }

      pos += direction * pxPerSec * dt;
      normalize();
      track.scrollLeft = pos;
      lastWritten = track.scrollLeft;
    }

    // ── User-interaction signals ──────────────────────────────────────────
    function markActive() {
      userActiveUntil = now() + IDLE_MS;
    }
    track.addEventListener('pointerdown', markActive);
    track.addEventListener('wheel', markActive, { passive: true });
    track.addEventListener('touchstart', markActive, { passive: true });
    track.addEventListener('touchmove', markActive, { passive: true });
    // Catches native touch momentum: scroll the engine didn't author.
    track.addEventListener(
      'scroll',
      function () {
        if (lastWritten < 0 || Math.abs(track.scrollLeft - lastWritten) > 2) {
          markActive();
        }
      },
      { passive: true }
    );
    // Mouse-only hover pause (mouseenter/leave don't fire for touch).
    track.addEventListener('mouseenter', function () {
      hovering = true;
    });
    track.addEventListener('mouseleave', function () {
      hovering = false;
    });

    // ── Layout/visibility observers ───────────────────────────────────────
    var rebuildTimer = null;
    function scheduleRebuild() {
      if (rebuildTimer) clearTimeout(rebuildTimer);
      rebuildTimer = setTimeout(buildClones, 150);
    }
    if (typeof ResizeObserver !== 'undefined') {
      // Set width changes when images/fonts finish loading.
      new ResizeObserver(scheduleRebuild).observe(set);
    }
    window.addEventListener('resize', scheduleRebuild);

    if (typeof IntersectionObserver !== 'undefined') {
      new IntersectionObserver(function (entries) {
        visible = entries[0] ? entries[0].isIntersecting : true;
      }).observe(track);
    }

    buildClones();
    rafId = requestAnimationFrame(frame);
  }

  function scan(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var nodes = scope.querySelectorAll('[data-spv-marcas-marquee]');
    for (var i = 0; i < nodes.length; i++) init(nodes[i]);
    // `root` itself may be the marquee (e.g. shopify:section:load target).
    if (root && root.matches && root.matches('[data-spv-marcas-marquee]')) init(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  // Theme editor re-renders the section on setting changes.
  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target || document);
  });
})();
